import { AsyncStateNotification } from '@ui/notifications';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { handleEnter } from '@lib/events';
import { useAddStakeholder } from '@hooks/stakeholders';

import { COPY_GENERIC, COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { coreActions, StakeholderType } from '@sonar-software/sonar-core';
import { PopoverModal, PopoverPosition } from '@ui/modals';
import React, { useCallback, useEffect } from 'react';
import { StakeholderEmail, StakeholderName, StakeholderTypeOption } from '@ui/form-items';
import { useAction, useForm } from '@hooks';

export interface AddNewStakeholderModalProps {
	isOpen: boolean;
	onCancel?: () => void;
	popoverPosition?: PopoverPosition;
	popoverTarget: React.RefObject<HTMLElement>;
}

interface AddNewStakeholderForm {
	email: string;
	name: string;
	type: string;
}

export const AddNewStakeholderModal: React.FC<AddNewStakeholderModalProps> = ({
	isOpen,
	onCancel,
	popoverPosition,
	popoverTarget
}) => {
	const { isIdle, isReceived, isRequested, request, reset, response } = useAddStakeholder();

	const { Form, form } = useForm<AddNewStakeholderForm>({
		required: [ 'name', 'type' ]
	});

	const loadStakeholders = useAction(coreActions.requestGetCoreStakeholders);

	useEffect(() => {
		if (!isReceived || !response) {
			return;
		}

		if (response.stakeholderId) {
			form.reset();
			loadStakeholders();
		}
	}, [ form, isReceived, loadStakeholders, response ]);

	const handleOnFinish = useCallback(() => {
		const { email, name, type } = form.getFieldsValue();

		if (!form.isValid) {
			return;
		}

		request({
			email,
			name,
			stakeholderType: type as StakeholderType
		});
	}, [ form, request ]);

	const handleOnValuesChange = useCallback(() => {
		if (!isIdle && !isRequested) {
			reset();
		}

		form.validate();
	}, [ form, isIdle, isRequested, reset ]);

	const handleCancel = useCallback(() => {
		if (onCancel) {
			onCancel();
		}

		form.reset();
	}, [ form, onCancel ]);

	return (
		<>
			<PopoverModal
				className='add-new-stakeholder-modal'
				confirmLoading={ isRequested }
				destroyOnHidden
				okButtonProps={ {
					disabled: !form.isValid,
					id: 'add-stakeholder-submit-button'
				} }
				okText={ COPY_SPECIFIC.ADD_NEW_STAKEHOLDER }
				onCancel={ handleCancel }
				onOk={ handleOnFinish }
				open={ isOpen }
				popoverPosition={ popoverPosition }
				popoverTarget={ popoverTarget }
				title={ COPY_SPECIFIC.ADD_A_NEW_STAKEHOLDER }>
				<Form
					form={ form }
					layout='vertical'
					onFinish={ handleOnFinish }
					onKeyUp={ handleEnter(() => form.submit()) }
					onValuesChange={ handleOnValuesChange }
					requiredMark={ false }>
					<StakeholderName
						autoFocus
						disabled={ isRequested }
						id='add-stakeholder-name'
						label={ COPY_SPECIFIC.NAME } />
					<StakeholderEmail
						disabled={ isRequested }
						id='add-stakeholder-email'
						label={ COPY_SPECIFIC.EMAIL } />
					<StakeholderTypeOption
						disabled={ isRequested }
						id='add-stakeholder-type'
						label={ COPY_SPECIFIC.TYPE } />
				</Form>
			</PopoverModal>
			<AsyncStateNotification
				byStatusCode={ [
					{
						options: { description: NOTIFICATIONS_COPY.STAKEHOLDER_CONFLICT },
						statusCode: ErrorOrFailStatusCode.Conflict
					}
				] }
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ NOTIFICATIONS_COPY.SUCCESS_POST_STAKEHOLDER }
				resetAction={ reset }
				stateSelector={ useAddStakeholder.selectors.state } />
		</>
	);
};
